import { Row, Table } from 'antd'
import ItemTable, { ItemTableColumn } from '../ItemTable'
import { ServerState, ViewItem, createViewItems, getEstimatedQuantities } from '../itemModel'
import { centsToDollarsDisplay } from '../../displayFormatters'
import CustomizableValue from '../CustomizableValue'
import OverrideNumericValueModal from '../../OverrideModals/OverrideNumericValueModal'
import { useContext, useRef, useState } from 'react'
import { ItemsContext } from '../ItemsRoot'
import { WorkareaItemTypes } from '../../../common/constants'

export type Props = {
  receiptId: number
  customTotal?: number
  onCustomTotalChanged: (customTotal?: number) => void
}
const ReceiptItems = (props: Props) => {
  const { jobItemsQuery, changeSet, jobData, focusedView } = useContext(ItemsContext)
  const { receiptId } = props
  const receiptItems = receiptId !== undefined
    ? jobItemsQuery.data?.actualItems.filter(x => x.receiptId === receiptId) ?? []
    : []
  const serverState: ServerState = {
    workareaItems: receiptItems,
    catalogItems: jobItemsQuery.data?.catalogItems ?? [],
    templateItems: [],
    estimatedQuantities: getEstimatedQuantities(jobItemsQuery.data?.estimatedItems ?? [])
  }
  const visibleColumns: ItemTableColumn[] = [
    'name',
    'workarea',
    'totalEstimatedQuantity',
    'quantity',
    'cost',
    'totalCost',
    'actions'
  ]

  const [isOverrideVisible, setIsOverrideVisible] = useState(false)
  const containerRef = useRef<any>()

  if (focusedView !== 'receipt') {
    return <></>
  }

  const filter = { receiptId: props.receiptId, workareaItemType: WorkareaItemTypes.Actual }
  const viewItems = createViewItems(changeSet, serverState, jobData.visitData, jobData.budgets,
    jobData.jobIsSold, jobData.workareas, filter)

  return <div ref={containerRef}>
    <ItemTable
      viewItems={viewItems}
      budgets={jobData.budgets}
      workareas={jobData.workareas}
      workareaItemType="actual"
      isLoading={jobItemsQuery.isLoading}
      visibleColumns={visibleColumns}
      serverState={serverState}
      customSummary={(items) => {
        const totals = items.reduce((acc, x) => {
          const quantity = x.isDeleted ? 0 : x.quantity
          return {
            subtotal: acc.subtotal + (x.isDeleted ? 0 : x.totalCost),
            tax: acc.tax + (x.costPlusTax - x.cost) * quantity,
            total: acc.total + x.costPlusTax * quantity
          }
        }, { subtotal: 0, tax: 0, total: 0 })
        const difference = (props.customTotal ?? totals.total) - totals.total

        const onSubmit = (value?: number) => {
          if (value === totals.total) {
            value = undefined
          }

          props.onCustomTotalChanged(value)
          setIsOverrideVisible(false)
        }

        return <>
          <Table.Summary.Row>
            <Table.Summary.Cell index={0} colSpan={5}><Row justify="end"><strong>Subtotal</strong></Row></Table.Summary.Cell>
            <Table.Summary.Cell index={1}><Row justify="end">{centsToDollarsDisplay(totals.subtotal)}</Row></Table.Summary.Cell>
            <Table.Summary.Cell index={2}></Table.Summary.Cell>
          </Table.Summary.Row>
          <Table.Summary.Row>
            <Table.Summary.Cell index={0} colSpan={5}><Row justify="end"><strong>Tax</strong></Row></Table.Summary.Cell>
            <Table.Summary.Cell index={1}><Row justify="end">{centsToDollarsDisplay(totals.tax)}</Row></Table.Summary.Cell>
            <Table.Summary.Cell index={2}></Table.Summary.Cell>
          </Table.Summary.Row>
          <Table.Summary.Row>
            <Table.Summary.Cell index={0} colSpan={5}><Row justify="end"><strong>Difference</strong></Row></Table.Summary.Cell>
            <Table.Summary.Cell index={1}><Row justify="end">{centsToDollarsDisplay(difference)}</Row></Table.Summary.Cell>
            <Table.Summary.Cell index={2}></Table.Summary.Cell>
          </Table.Summary.Row>
          <Table.Summary.Row>
            <Table.Summary.Cell index={0} colSpan={5}><Row justify="end"><strong>Total</strong></Row></Table.Summary.Cell>
            <Table.Summary.Cell index={1}>
              <Row justify="end">
                <CustomizableValue
                  canCustomize={true}
                  isCustomized={props.customTotal !== undefined && props.customTotal !== null}
                  item={{ isEmptyRow: false, isEntireRowDisabled: false } as ViewItem}
                  openOverrideDialog={() => setIsOverrideVisible(true)}
                >
                  {centsToDollarsDisplay(props.customTotal ?? totals.total)}
                </CustomizableValue>
              </Row>
            </Table.Summary.Cell>
            <Table.Summary.Cell index={2}></Table.Summary.Cell>
          </Table.Summary.Row>
          <OverrideNumericValueModal
            visible={isOverrideVisible}
            currentValue={props.customTotal ?? totals.total}
            defaultValue={totals.total}
            label='actual total price'
            name={'this receipt'}
            type='cents'
            onCancel={() => setIsOverrideVisible(false)}
            onSubmit={onSubmit}
            onReset={() => onSubmit(undefined)}
            getContainer={() => containerRef.current}
          />
        </>
      }}
    />
  </div>
}
export default ReceiptItems
